/**
 * App Shell
 * Global body-level Alpine component.
 * Manages nav drawer open state, current path tracking,
 * mobile breakpoint flag, and an isLoaded hook used by
 * entrance animations across the site.
 */

import gsap from 'gsap';

document.addEventListener('alpine:init', () => {
  window.Alpine.data('appShell', () => ({
    navOpen: false,
    current: window.location.pathname,
    isMobile: window.matchMedia('(max-width: 720px)').matches,
    isLoaded: false,

    init() {
      this.isLoaded = true;

      // Keep isMobile in sync with viewport
      window.addEventListener('resize', () => {
        this.isMobile = window.innerWidth < 720;
      });

      // Animate menu label on navOpen change — letters stagger in one by one
      this.$watch('navOpen', (isOpen) => {
        const label = this.$refs.menuLabel;
        if (!label) return;

        const nextText = isOpen ? 'CLOSE' : 'MENU';

        const staggerDir = isOpen ? 'start' : 'end';

        // Fade out existing letters
        const existing = label.querySelectorAll('span');
        if (existing.length) {
          gsap.to(existing, {
            opacity: 0,
            duration: 0.08,
            stagger: { each: 0.02, from: staggerDir },
            ease: 'power2.in',
            onComplete: () => buildLetters(),
          });
        } else {
          buildLetters();
        }

        function buildLetters() {
          // Replace content with one span per letter
          label.innerHTML = nextText
            .split('')
            .map(char => `<span style="display:inline-block;opacity:0">${char}</span>`)
            .join('');

          // Stagger each letter in from the same direction
          gsap.to(label.querySelectorAll('span'), {
            opacity: 1,
            duration: 0.14,
            stagger: { each: 0.03, from: staggerDir },
            ease: 'power2.out',
          });
        }
      });

      // Keep current in sync with Swup page changes.
      // visit:start fires the moment a link is clicked so the active
      // class updates immediately; content:replace keeps it correct
      // for browser back/forward navigation.
      if (window.swup) {
        window.swup.hooks.on('visit:start', (visit) => {
          this.current = new URL(visit.to.url, window.location.origin).pathname;
        });
        window.swup.hooks.on('content:replace', () => {
          this.current = window.location.pathname;
          this.navOpen = false;
        });
      }
    },

    toggleNav() {
      this.navOpen = !this.navOpen;
    },

    closeNav() {
      this.navOpen = false;
    },

    // Toggle the "Start a project" overlay. Manages the show class
    // directly (rather than routing through the URL hash) so clicking
    // the trigger never leaves `#start-a-project` in the address bar.
    // Deep-links to `#start-a-project` still work — main.js's checkHash
    // listener handles that separately on page load / hashchange.
    toggleStartProject(event) {
      event?.preventDefault();
      const el = document.getElementById('start-a-project');
      if (!el) return;
      el.classList.toggle('show');
      if (window.location.hash === '#start-a-project') {
        history.replaceState(null, '', window.location.pathname);
      }
    },

    // Close the drawer when focus leaves it — but keep it open if the
    // new focus target is the menu-toggle (so shift-tabbing back to the
    // hamburger doesn't feel like the drawer is fighting the user) or
    // if focus dropped to nothing (window blur / body).
    handleNavFocusOut(event) {
      if (!this.navOpen) return;
      const next = event.relatedTarget;
      if (!next) return;
      if (event.currentTarget.contains(next)) return;
      if (next.matches && next.matches('.menu-toggle')) return;
      this.navOpen = false;
    },
  }));
});
